/**
 * Batches individual .load(id) calls into bulk fetches.
 *
 * All calls within a single animation frame are collected and dispatched
 * together. This pairs with React Query's per-key useQuery observers:
 * each entity gets a real observer (so invalidation triggers refetch),
 * while the loader prevents N individual API calls.
 *
 * Usage:
 *   const loader = createBatchLoader<PlainMember>(async (ids) => {
 *       const resp = await api.listMembers({ filter: { userIds: ids } });
 *       return new Map(resp.members.map(m => [m.userId, toPlain(m)]));
 *   });
 *   const member = await loader.load(userId);
 * 
 * And yes, this is basically AI-generated https://www.npmjs.com/package/dataloader
 */
export type BatchLoader<T> = { load: (key: string) => Promise<T | undefined> };

type Pending<T> = {
    key: string;
    resolve: (value: T | undefined) => void;
    reject: (error: unknown) => void;
};

export function createBatchLoader<T>(
    batchFn: (ids: string[]) => Promise<Map<string, T>>,
    maxBatchSize = 25,
): BatchLoader<T> {
    let queue: Pending<T>[] = [];
    let scheduled = false;

    const flush = async () => {
        const batch = queue;
        queue = [];
        scheduled = false;

        try {
            const results = new Map<string, T>();
            for (let i = 0; i < batch.length; i += maxBatchSize) {
                const chunk = batch.slice(i, i + maxBatchSize).map((p) => p.key);
                const chunkResults = await batchFn(chunk);
                for (const [k, v] of chunkResults) {
                    results.set(k, v);
                }
            }
            for (const p of batch) {
                p.resolve(results.get(p.key));
            }
        } catch (error) {
            for (const p of batch) {
                p.reject(error);
            }
        }
    };

    return {
        load(key: string) {
            return new Promise<T | undefined>((resolve, reject) => {
                queue.push({ key, resolve, reject });
                if (!scheduled) {
                    scheduled = true;
                    requestAnimationFrame(flush);
                }
            });
        },
    };
}
